import React, { useState, useEffect, useCallback } from 'react';
import { clientsService, CreateClientData } from '@services/clientsService';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, FormGroup, Badge, LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  People as PeopleIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

interface Client {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  gdprConsent: {
    marketing: boolean;
    sms: boolean;
    email: boolean;
  };
  notes?: string;
  status: 'active' | 'inactive' | 'blocked';
  createdAt: string;
  lastVisit?: string;
  totalVisits: number;
  totalSpent: number;
  patients: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`clients-tabpanel-${index}`}
      aria-labelledby={`clients-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientsManagement: React.FC = () => {
  const { user: _user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Client form state
  const [newClient, setNewClient] = useState<{
    name: string;
    phone: string;
    email: string;
    address: string;
    gdprConsent: {
      marketing: boolean;
      sms: boolean;
      email: boolean;
    };
    notes: string;
    status: 'active' | 'inactive' | 'blocked';
  }>({
    name: '',
    phone: '',
    email: '',
    address: '',
    gdprConsent: {
      marketing: false,
      sms: false,
      email: false
    },
    notes: '',
    status: 'active'
  });

  // Functie pentru incarcarea clientilor
  const loadClients = useCallback(async () => {
      setLoading(true);
      
      try {
        // Incarcare date reale de la backend
        const result = await clientsService.getClients({
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          status: selectedStatus === 'all' ? undefined : selectedStatus as any
        });
        
        // Transformare date din backend in formatul asteptat de componenta
        const transformedClients = result.clients.map(client => ({
          id: client.id,
          name: `${client.firstName} ${client.lastName}`.trim(),
          phone: client.phone,
          email: client.email,
          address: client.address ? 
            `${client.address.street}, ${client.address.city}, ${client.address.county}` : '',
          gdprConsent: {
            marketing: client.communicationPreferences?.email || false,
            sms: client.communicationPreferences?.sms || false,
            email: client.communicationPreferences?.email || false
          },
          notes: '',
          status: client.status === 'suspended' ? 'blocked' : client.status as 'active' | 'inactive' | 'blocked',
          createdAt: client.createdAt,
          lastVisit: client.lastVisit,
          totalVisits: 0, // Va fi calculat din backend
          totalSpent: client.totalSpent || 0,
          patients: client.patientCount || 0
        }));
        
        setClients(transformedClients);
      } catch (error) {
        console.error('Eroare la incarcarea clientilor:', error);
        // In caz de eroare, afisam array gol
        setClients([]);
      }

      setLoading(false);
  }, [page, rowsPerPage, searchTerm, selectedStatus]);

  // Incarcare clienti - foloseste API real
  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'inactive':
        return 'Inactiv';
      case 'blocked':
        return 'Blocat';
      default:
        return status;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateClient = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewClient({
      name: '',
      phone: '',
      email: '',
      address: '',
      gdprConsent: {
        marketing: false,
        sms: false,
        email: false
      },
      notes: '',
      status: 'active'
    });
  };

  const handleSaveClient = async () => {
    try {
      // Validare campuri obligatorii
      if (!newClient.name.trim() || !newClient.phone.trim()) {
        alert('Numele si telefonul sunt obligatorii!');
        return;
      }

      // Pregatire date pentru backend
      const [firstName, ...lastNameParts] = newClient.name.trim().split(' ');
      const lastName = lastNameParts.join(' ') || '';
      
      const clientData: CreateClientData = {
        firstName: firstName,
        lastName: lastName,
        email: newClient.email,
        phone: newClient.phone,
        address: newClient.address ? {
          street: newClient.address,
          city: '',
          county: '',
          postalCode: '',
          country: 'Romania'
        } : undefined,
        tags: [],
        gdprConsent: newClient.gdprConsent.sms || newClient.gdprConsent.email || newClient.gdprConsent.marketing,
        communicationPreferences: {
          sms: newClient.gdprConsent.sms,
          email: newClient.gdprConsent.email,
          whatsapp: false
        }
      };

      await clientsService.createClient(clientData);
      await loadClients();
      handleCloseCreateDialog();
    } catch (error: any) {
      console.error('Eroare la salvarea clientului:', error);
      alert(error?.message || 'Eroare la salvarea clientului. Va rugam sa incercati din nou.');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.phone.includes(searchTerm) ||
                         (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = selectedStatus === 'all' || client.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  const activeClients = filteredClients.filter(c => c.status === 'active');
  const inactiveClients = filteredClients.filter(c => c.status === 'inactive');
  const allClients = filteredClients;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca clientii...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Clienti (Proprietari)
          </Typography>
          <Typography variant="body1" color="text.secondary">
            CRM de baza - CRUD, istoric programari/plati, preferinte comunicare, consimtamant GDPR
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClient}
          sx={{ borderRadius: 2 }}
        >
          Client Nou
        </Button>
      </Box>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                placeholder="Cauta nume, telefon sau email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                  <MenuItem value="blocked">Blocat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                sx={{ height: 56, borderRadius: 2 }}
              >
                Filtre Avansate
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Actualizeaza">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="clients tabs">
            <Tab 
              label={
                <Badge badgeContent={activeClients.length} color="success">
                  Activi
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={inactiveClients.length} color="warning">
                  Inactivi
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={allClients.length} color="primary">
                  Toti
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Ultima Vizita</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Consimtamant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <PeopleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {client.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.patients} pacient{client.patients !== 1 ? 'i' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" />
                            {client.phone}
                          </Typography>
                          {client.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" />
                              {client.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.lastVisit || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {client.totalVisits} vizite
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.totalSpent} RON
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {client.gdprConsent.sms && (
                            <Chip label="SMS" size="small" color="info" />
                          )}
                          {client.gdprConsent.email && (
                            <Chip label="Email" size="small" color="secondary" />
                          )}
                          {client.gdprConsent.marketing && (
                            <Chip label="Marketing" size="small" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(client.status)}
                          color={getStatusColor(client.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Istoric">
                            <IconButton size="small">
                              <CalendarIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activeClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Ultima Vizita</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Consimtamant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inactiveClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            <PeopleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {client.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.patients} pacient{client.patients !== 1 ? 'i' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" />
                            {client.phone}
                          </Typography>
                          {client.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" />
                              {client.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.lastVisit || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {client.totalVisits} vizite
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.totalSpent} RON
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {client.gdprConsent.sms && (
                            <Chip label="SMS" size="small" color="info" />
                          )}
                          {client.gdprConsent.email && (
                            <Chip label="Email" size="small" color="secondary" />
                          )}
                          {client.gdprConsent.marketing && (
                            <Chip label="Marketing" size="small" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(client.status)}
                          color={getStatusColor(client.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reactiveaza">
                            <IconButton size="small">
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={inactiveClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Ultima Vizita</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Consimtamant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allClients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: client.status === 'active' ? 'primary.light' : 
                                    client.status === 'inactive' ? 'warning.light' : 'error.light'
                          }}>
                            <PeopleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {client.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.patients} pacient{client.patients !== 1 ? 'i' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" />
                            {client.phone}
                          </Typography>
                          {client.email && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" />
                              {client.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.lastVisit || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {client.totalVisits} vizite
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.totalSpent} RON
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {client.gdprConsent.sms && (
                            <Chip label="SMS" size="small" color="info" />
                          )}
                          {client.gdprConsent.email && (
                            <Chip label="Email" size="small" color="secondary" />
                          )}
                          {client.gdprConsent.marketing && (
                            <Chip label="Marketing" size="small" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(client.status)}
                          color={getStatusColor(client.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Vezi detalii">
                            <IconButton size="small">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editeaza">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Istoric">
                            <IconButton size="small">
                              <CalendarIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Client Nou
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Nume complet */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Nume complet"
                value={newClient.name}
                onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                required
              />
            </Grid>

            {/* Telefon si Email */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefon"
                value={newClient.phone}
                onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
              />
            </Grid>

            {/* Adresa */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Adresa"
                multiline
                rows={2}
                value={newClient.address}
                onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                placeholder="Strada, numarul, orasul, judetul..."
              />
            </Grid>

            {/* Consimtamant GDPR */}
            <Grid size={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Consimtamant GDPR
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.sms}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, sms: e.target.checked}
                      })}
                    />
                  }
                  label="SMS - pentru notificari despre programari si servicii"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.email}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, email: e.target.checked}
                      })}
                    />
                  }
                  label="Email - pentru comunicari si rapoarte"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.marketing}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, marketing: e.target.checked}
                      })}
                    />
                  }
                  label="Marketing - pentru oferte si promotii"
                />
              </FormGroup>
            </Grid>

            {/* Status */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newClient.status}
                  onChange={(e) => setNewClient({...newClient, status: e.target.value as 'active' | 'inactive' | 'blocked'})}
                  label="Status"
                >
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                  <MenuItem value="blocked">Blocat</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Observatii */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Observatii"
                multiline
                rows={3}
                value={newClient.notes}
                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                placeholder="Observatii suplimentare despre client..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseCreateDialog} color="inherit">
            Anuleaza
          </Button>
          <Button 
            onClick={handleSaveClient} 
            variant="contained" 
            color="primary"
          >
            Salveaza Client
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsManagement;

